import { EVENTS } from '@archetype-themes/utils/events'
import Modals from '@archetype-themes/modules/modal'

class ProductVariantVideos extends HTMLElement {
    constructor() {
      super()
      
      this.selectors = {
        videoContainer: '[data-variant-video]',
        currentVariantJson: '[data-current-variant-json]',
        header: 'h4',
        videoWrapper: '.variant-video-container:not(.hide)',
        island: 'is-land',
        video: '.variant-video',
        modalTrigger: '.variant__modal-video',
        modalClose: '[data-tool-tip-close]'
      }
  
      this.classes = {
        hidden: 'hide',
        loading: 'is-loading',
        mobileHidden: 'hide-mobile'
      }

      this.mediaQuery = window.matchMedia('(max-width: 749px)')
      this.modal = null
      this.closeButtonHandler = this.closeButtonHandler.bind(this)
    }
  
    connectedCallback() {
      this.abortController = new AbortController()
      this.currentVariant = JSON.parse(this.querySelector(this.selectors.currentVariantJson)?.textContent)
      
      this.initVariants()
      this.showVariantVideo(this.currentVariant?.id)
      this.initModal()
      
      // Wait for DOM updates before checking header visibility
      requestAnimationFrame(() => {
        this.updateHeaderVisibility()
      })

      // Handle mobile visibility
      this.handleMobileVisibility()
      this.mediaQuery.addEventListener('change', this.handleMobileVisibility.bind(this))
    }
  
    disconnectedCallback() {
      this.abortController.abort()
      this.mediaQuery.removeEventListener('change', this.handleMobileVisibility.bind(this))
      
      // Remove modal close event listener
      document.removeEventListener('modalClose.VideoModal', this.closeButtonHandler)
    }

    handleMobileVisibility() {
      const isMobile = this.mediaQuery.matches
      const videoContainers = this.querySelectorAll(this.selectors.videoContainer)
      
      videoContainers.forEach(container => {
        if (isMobile) {
          container.classList.add(this.classes.mobileHidden)
        } else {
          container.classList.remove(this.classes.mobileHidden)
        }
      })
    }

    initModal() {
      // Create modal HTML if it doesn't exist
      if (!document.getElementById('VideoModal')) {
        const modalHTML = `
          <div id="VideoModal" class="modal">
            <div class="modal__inner">
              <div class="modal__centered page-width text-center">
                <div class="modal__centered-content">
                  <div class="video-wrapper video-wrapper--modal">
                    <div id="VideoHolder"></div>
                  </div>
                </div>
              </div>
              <button class="tool-tip__close btn btn--circle btn--icon js-modal-close" data-tool-tip-close="">
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close icon--line" viewBox="0 0 64 64" width="20" height="20">
                  <path d="m19 17.61 27.12 27.13m0-27.13L19 44.74" stroke-miterlimit="10" fill="none" stroke="currentColor"></path>
                </svg>
              </button>
            </div>
          </div>
        `
        document.body.insertAdjacentHTML('beforeend', modalHTML)
      }

      // Initialize modal
      this.modal = new Modals('VideoModal', 'video-modal', {
        closeOffContentClick: true,
        bodyOpenClass: ['modal-open', 'video-modal-open'],
        solid: true
      })

      // Add modal close handler
      document.addEventListener('modalClose.VideoModal', this.closeButtonHandler)

      // Add click handler to modal trigger button
      const modalTrigger = document.querySelector(this.selectors.modalTrigger)
      if (modalTrigger) {
        modalTrigger.addEventListener('click', (e) => {
          e.preventDefault()
          this.openVideoModal()
        })
      }
    }

    closeButtonHandler() {
      const videoHolder = document.getElementById('VideoHolder')
      if (videoHolder) {
        const video = videoHolder.querySelector('video')
        if (video) {
          video.pause()
          video.currentTime = 0
        }
        videoHolder.innerHTML = ''
      }
    }

    openVideoModal() {
      const currentVideo = this.querySelector(`[data-variant-video="${this.currentVariant?.id}"]`)
      if (!currentVideo) return

      const video = currentVideo.querySelector(this.selectors.video)
      if (!video) return

      const videoHolder = document.getElementById('VideoHolder')
      if (!videoHolder) return

      // Clone the video element
      const videoClone = video.cloneNode(true)
      videoClone.classList.remove(this.classes.hidden)
      videoClone.classList.remove(this.classes.mobileHidden)

      // Clear previous content and add new video
      videoHolder.innerHTML = ''
      videoHolder.appendChild(videoClone)

      // Open modal
      this.modal.open()

      // Play video
      const playPromise = videoClone.play()
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Video autoplay failed:', error)
        })
      }
    }
  
    initVariants() {
      document.addEventListener(
        `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
        this.handleVariantChange.bind(this),
        { signal: this.abortController.signal }
      )
    }
  
    handleVariantChange(evt) {
      const variant = evt?.detail?.variant
      if (!variant) return
      
      this.currentVariant = variant
      this.showVariantVideo(variant.id)
      // Check header visibility after variant change
      requestAnimationFrame(() => {
        this.updateHeaderVisibility()
      })
    }
  
    showVariantVideo(variantId) {
      if (!variantId) return
  
      // Hide all videos and their islands
      this.querySelectorAll(this.selectors.videoContainer).forEach(container => {
        container.classList.add(this.classes.hidden)
        const island = container.closest(this.selectors.island)
        if (island) {
          island.classList.add(this.classes.hidden)
        }
      })
  
      // Show the video for the current variant
      const currentVideo = this.querySelector(`[data-variant-video="${variantId}"]`)
      if (currentVideo) {
        currentVideo.classList.remove(this.classes.hidden)
        const island = currentVideo.closest(this.selectors.island)
        if (island) {
          island.classList.remove(this.classes.hidden)
        }
      }
    }

    updateHeaderVisibility() {
      const header = this.querySelector(this.selectors.header)
      if (!header) return

      // Check for any visible videos
      const hasVisibleVideo = this.querySelector(this.selectors.videoWrapper) !== null
      
      if (hasVisibleVideo) {
        header.classList.remove(this.classes.hidden)
      } else {
        header.classList.add(this.classes.hidden)
      }
    }
}
  
customElements.define('product-variant-videos', ProductVariantVideos)